import { useState, type DragEvent } from "react";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { NativeSelect } from "@mantine/core";
import { Link } from "react-router-dom";
import { apiClient, apiErrorMessage, callFunction } from "../api/client";
import type { ImportField, ImportOutcome, ImportValue, Job } from "../api/types";
import type { SignedIn } from "../lib/auth";
import { useUserAccounts } from "../lib/campaign";
import { FILES_BASE } from "../lib/config";
import { shortDate } from "../lib/format";
import {
  FORMAT_WORDS, MARKS, VENDOR_FORMATS, importData, marksSet, readData, sourceFromFile,
} from "../lib/importRead";
import { PAGE_SIZE, useListParams } from "../lib/useListParams";
import { Badge } from "../ui/Badge";
import { Button } from "../ui/Button";
import { useConfirm } from "../ui/Dialog";
import { DataTable, type ColumnSpec } from "../ui/DataTable";
import { FiltersPanel, Toolbar } from "../ui/Toolbar";
import { ImportPreview } from "../components/ImportPreview";
import { JobProgress, jobSummary, useJob } from "../components/JobProgress";
import { Spinner } from "../components/Spinner";

/* Bring a file into the lake.
 *
 * Loading a list she built by hand, the researcher needs to see what EVIE
 * will do with each row before it does it, so her list updates records
 * instead of copying them. So the common path is: upload any file, EVIE
 * reads it, a preview shows what loading it would do, one press loads it,
 * and Undo takes it back. A vendor's export can be named up front, or EVIE
 * names it from its columns and offers that loader.
 *
 * The form is one panel: the file and what it is, and beside them the values
 * for every row, which are set before the upload because a vendor's export
 * loads without a preview. James, 2026-10-23: they are optional and blank,
 * they work for every format, and each fills only what the file leaves
 * blank (engine ingestion/import_marks.py).
 *
 * The upload itself is three steps, and the middle one does not touch this
 * box at all: ask the file service for somewhere to put the file, send the
 * file straight to that place, then ask the API to read or load it. A 40 MB
 * workbook goes from this browser to storage without anything in between
 * having to hold it.
 *
 * James, 2026-10-13: automated by default, approval before it writes, and
 * an undo anyone can press because it removes only what the import added.
 * docs/ux-decisions.md, Import. */

const AUTO = "auto";

function sizeWords(bytes: number): string {
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024)).toLocaleString()} KB`;
}

export function Import({ session }: { session: SignedIn }) {
  const token = session.idToken;
  const [format, setFormat] = useState<string>(AUTO);
  const [file, setFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [marks, setMarks] = useState<Record<string, string>>({});
  const [step, setStep] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  // The file being read or loaded: its key in storage and its name.
  const [upload, setUpload] = useState<{ key: string; name: string } | null>(null);
  const [readJobId, setReadJobId] = useState<string | null>(null);
  const [loadJobId, setLoadJobId] = useState<string | null>(null);
  const [source, setSource] = useState("");
  /* Blank until a person chooses an earlier date. The engine dates a blank
     one by Phoenix's calendar and refuses one after today (engine migration
     294). It was the browser's date, which was tomorrow for some readers. */
  const [asOf, setAsOf] = useState("");
  const queryClient = useQueryClient();

  const fields = useQuery({
    queryKey: ["import-fields"],
    queryFn: async () => {
      const { data, error } = await apiClient(token).from("import_field")
        .select("target,kind,label,position").order("position").limit(100);
      if (error) throw new Error(apiErrorMessage(error));
      return (data ?? []) as ImportField[];
    },
  });
  const choices = useQuery({
    queryKey: ["import-values"],
    staleTime: 60 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await apiClient(token).from("import_value")
        .select("target,value,label,position").order("position").limit(100);
      if (error) throw new Error(apiErrorMessage(error));
      return (data ?? []) as ImportValue[];
    },
  });

  const readJob = useJob(token, readJobId);
  const read = readJob.data?.status === "done" ? readData(readJob.data) : null;
  const setMark = (field: string, value: string) => setMarks((m) => ({ ...m, [field]: value }));

  function choose(f: File | null) {
    setFile(f);
    setProblem(null);
    // A new file clears the last one's read, so no Preview is shown under a
    // file it does not describe. Until 2026-10-27 the last Preview stayed up,
    // and its Load it as button loaded the last file.
    if (f) {
      setReadJobId(null);
      setUpload(null);
    }
  }

  function drop(e: DragEvent<HTMLLabelElement>) {
    e.preventDefault();
    setDragging(false);
    choose(e.dataTransfer.files?.[0] ?? null);
  }

  async function send() {
    if (!file) return;
    setProblem(null);
    setReadJobId(null);
    setLoadJobId(null);
    setUpload(null);
    // The step in hand, kept here as well as in state, because the catch below
    // runs inside this call and reads the state as it was when the call began.
    // It said "Starting" for every failure until 2026-10-13.
    let current = "Starting";
    const at = (s: string) => { current = s; setStep(s); };
    const fmt = format === AUTO ? "generic_csv" : format;
    try {
      at("Getting an upload link");
      const slotRes = await fetch(
        `${FILES_BASE}/upload?name=${encodeURIComponent(file.name)}&format=${encodeURIComponent(fmt)}`,
        { method: "POST", headers: { Authorization: `Bearer ${token}` } },
      );
      const slot = await slotRes.json().catch(() => ({}));
      if (!slotRes.ok) throw new Error(slot.message || `the file service answered ${slotRes.status}`);
      if (typeof slot.max_bytes === "number" && file.size > slot.max_bytes) {
        throw new Error(
          `That file is ${Math.round(file.size / 1048576)} MB and the limit is ` +
          `${Math.round(slot.max_bytes / 1048576)} MB. Send it in parts, or ask for the limit to be raised.`,
        );
      }

      at("Sending the file");
      const put = await fetch(slot.url, { method: "PUT", body: file });
      if (!put.ok) throw new Error(`the file did not upload: storage answered ${put.status}`);

      const key = String(slot.key);
      setUpload({ key, name: file.name });
      if (format === AUTO) {
        at("Asking EVIE to read it");
        const src = sourceFromFile(file.name);
        setSource(src);
        const id = await callFunction<string>(token, "read_csv", { p_key: key, p_source: src, p_as_of: asOf || null });
        setReadJobId(id);
      } else {
        at("Queueing it");
        const id = await callFunction<string>(token, "register_import", {
          p_key: key, p_format: format, p_marks: marksSet(marks),
        });
        setLoadJobId(id);
        queryClient.invalidateQueries({ queryKey: ["imports"] });
      }
      setStep(null);
      setFile(null);
    } catch (e) {
      // Which of the steps failed is the whole diagnosis, so the message keeps
      // it rather than saying the upload failed.
      setProblem(`${current}: ${e instanceof Error ? e.message : "it did not work"}`);
      setStep(null);
    }
  }

  async function loadAsVendor(fmt: string) {
    if (!upload) return;
    setProblem(null);
    try {
      const id = await callFunction<string>(token, "register_import", {
        p_key: upload.key, p_format: fmt, p_marks: marksSet(marks),
      });
      setReadJobId(null);
      setLoadJobId(id);
    } catch (e) {
      setProblem(e instanceof Error ? e.message : String(e));
    }
  }

  const formatOptions = [
    { value: AUTO, label: "Let EVIE read it" },
    ...VENDOR_FORMATS.map((f) => ({ value: f.key, label: f.label })),
  ];
  const hint = format === AUTO
    ? "A CSV or an Excel workbook. EVIE works out what its columns are."
    : VENDOR_FORMATS.find((f) => f.key === format)?.hint ?? "";
  const vendor = read?.detectedFormat ? VENDOR_FORMATS.find((f) => f.key === read.detectedFormat) : null;
  const optionsFor = (field: string) => [{ value: "", label: "Not set" },
    ...(choices.data ?? []).filter((c) => c.target === field)
      .map((c) => ({ value: c.value ?? "", label: c.label ?? c.value ?? "" }))];

  return (
    <main className="im">
      <h1>Import a file</h1>
      <p className="muted im-lead">
        Upload a vendor export, or any CSV or Excel workbook. EVIE shows what loading it would do before anything is written.
      </p>

      <section className="im-panel" aria-label="The file to import">
        <div className="im-panel-grid">
          <div className="im-part">
            <h2>File</h2>
            <label
              className={`im-drop${dragging ? " im-drop-over" : ""}${file ? " im-drop-chosen" : ""}`}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={drop}
            >
              <input type="file" className="sr-only" aria-label="The file"
                     onChange={(e) => { choose(e.target.files?.[0] ?? null); e.currentTarget.value = ""; }} />
              {file ? (
                <>
                  <strong className="im-drop-name">{file.name}</strong>
                  <span className="muted">{sizeWords(file.size)}. Choose another</span>
                </>
              ) : (
                <>
                  <strong>Drop a file here, or choose one</strong>
                  <span className="muted">One file at a time</span>
                </>
              )}
            </label>
            <NativeSelect id="fmt" label="What it is" description={hint} value={format} data={formatOptions}
                          onChange={(e) => setFormat(e.currentTarget.value)} />
          </div>

          <div className="im-part">
            <h2>Values for every row</h2>
            <p className="muted im-note">Optional. Each fills records the file leaves blank.</p>
            <div className="im-marks">
              {MARKS.map((m) => (
                <NativeSelect
                  key={m.field}
                  label={m.label}
                  description={m.field.startsWith("person.") ? "For people whose title gives none." : undefined}
                  value={marks[m.field] ?? ""}
                  data={optionsFor(m.field)}
                  disabled={!choices.data}
                  onChange={(e) => setMark(m.field, e.currentTarget.value)}
                />
              ))}
            </div>
            {choices.isError ? (
              <p className="refusal" role="alert">The choices did not load. {(choices.error as Error).message}</p>
            ) : null}
          </div>
        </div>

        <div className="im-go">
          <Button kind="orange" onClick={() => void send()} disabled={!file || step !== null} loading={step !== null}>
            {step ? step : format === AUTO ? "Upload and read it" : "Upload and load it"}
          </Button>
          <span className="muted">
            {!file ? "Choose a file to start."
              : format === AUTO ? "EVIE reads it first and shows what loading it would do."
              : "It loads as soon as it is uploaded."}
          </span>
        </div>
        {problem ? <p className="refusal" role="alert">{problem}</p> : null}
      </section>

      {readJobId && !loadJobId ? (
        readJob.data?.status === "done" && read ? (
          vendor ? (
            <section className="ip" aria-label="Preview">
              <h2>Preview</h2>
              <p className="ip-says">{read.says}</p>
              <div className="bar">
                <Button kind="orange" onClick={() => void loadAsVendor(vendor.key)}>Load it as {FORMAT_WORDS[vendor.key] ?? vendor.label}</Button>
                <span className="muted">The {vendor.label} loader reads it, with the values for every row set above.</span>
              </div>
            </section>
          ) : upload ? (
            <ImportPreview
              key={readJobId} token={token} job={readJob.data} uploadKey={upload.key}
              fields={fields.data ?? []} source={source} asOf={asOf}
              marks={marks} choices={choices.data ?? []} onMark={setMark}
              onReread={(id, src, when) => { setSource(src); setAsOf(when); setReadJobId(id); }}
              onLoaded={(id) => { setLoadJobId(id); queryClient.invalidateQueries({ queryKey: ["imports"] }); }}
            />
          ) : null
        ) : (
          <JobProgress key={readJobId} token={token} jobId={readJobId}
                       waiting="File queued for reading." running="Reading the file. A large file takes longer."
                       done="Read." />
        )
      ) : null}

      {loadJobId ? (
        <JobProgress key={loadJobId} token={token} jobId={loadJobId}
                     waiting="File queued." running="Loading the file."
                     done="File loaded. It is at the top of the imports below."
                     onDone={() => {
                       queryClient.invalidateQueries({ queryKey: ["imports"] });
                       queryClient.invalidateQueries({ queryKey: ["import-counts"] });
                       setReadJobId(null);
                       setUpload(null);
                     }} />
      ) : null}

      <History token={token} />
    </main>
  );
}

/* What has been imported, newest first, a page at a time: the file, its
   format, where it stands, how many records it added, updated, matched and
   held for review (api.import_outcome, engine migration 230), who loaded it
   and when. Records opens the import's records. Undo is on any import whose
   loader recorded its signal: a CSV EVIE read, and since 2026-10-16 a vendor
   file. Search is by file name, on the server. */

const FILTERS = ["format"] as const;

type Counts = { added: number; updated: number; matched: number; held: number };

function History({ token }: { token: string }) {
  const queryClient = useQueryClient();
  const params = useListParams("queued_at", FILTERS, false, "", "ih.");
  const [filtersOpen, setFiltersOpen] = useState(Boolean(params.filters.format));
  const [undoJobId, setUndoJobId] = useState<string | null>(null);
  const { ask, dialog } = useConfirm();
  const accounts = useUserAccounts(token);

  const narrowed = (opts?: { count: "exact"; head?: boolean }) => {
    let q = apiClient(token).from("job")
      .select("id,kind,status,params,requested_by,result,error,queued_at,finished_at,artifact_key", opts)
      .eq("kind", "file_import");
    const s = params.search.trim().replace(/[(),*"\\]/g, " ").trim();
    if (s) q = q.ilike("params->>key", `*${s}*`);
    if (params.filters.format) q = q.eq("params->>format", params.filters.format);
    return q;
  };

  const history = useQuery({
    queryKey: ["imports", params.page, params.search, params.filters],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const from = (params.page - 1) * PAGE_SIZE;
      const { data, error } = await narrowed()
        .order("queued_at", { ascending: false, nullsFirst: false })
        .range(from, from + PAGE_SIZE - 1);
      if (error) throw new Error(apiErrorMessage(error));
      return (data ?? []) as Job[];
    },
  });
  const total = useQuery({
    queryKey: ["imports", "count", params.search, params.filters],
    queryFn: async () => {
      const { error, count } = await narrowed({ count: "exact", head: true });
      if (error) throw new Error(apiErrorMessage(error));
      return count ?? null;
    },
  });
  const rows = history.data ?? [];
  const ids = rows.map((j) => j.id).filter((x): x is string => Boolean(x));

  // What each import on the page did to its records, summed across kinds.
  const counts = useQuery({
    queryKey: ["import-counts", ids],
    enabled: ids.length > 0,
    queryFn: async () => {
      const { data, error } = await apiClient(token).from("import_outcome")
        .select("job_id,entity_type,outcome,held,records").in("job_id", ids).limit(ids.length * 12);
      if (error) throw new Error(apiErrorMessage(error));
      const by = new Map<string, Counts>();
      for (const r of (data ?? []) as ImportOutcome[]) {
        const c = by.get(r.job_id ?? "") ?? { added: 0, updated: 0, matched: 0, held: 0 };
        const n = r.records ?? 0;
        if (r.held) c.held += n;
        else if (r.outcome === "added") c.added += n;
        else if (r.outcome === "updated") c.updated += n;
        else c.matched += n;
        by.set(r.job_id ?? "", c);
      }
      return by;
    },
  });

  // The undos, matched to their imports here, because each is its own job.
  const undos = useQuery({
    queryKey: ["import-undos"],
    queryFn: async () => {
      const { data, error } = await apiClient(token)
        .from("job")
        .select("id,status,params,error,result,queued_at,finished_at")
        .eq("kind", "undo_import")
        .order("queued_at", { ascending: false, nullsFirst: false })
        .limit(PAGE_SIZE * 2);
      if (error) throw new Error(apiErrorMessage(error));
      return (data ?? []) as Job[];
    },
  });

  const undoOf = (importId: string | null | undefined): Job | null => {
    if (!importId) return null;
    return (undos.data ?? []).find((u) => {
      const p = (u.params ?? {}) as Record<string, unknown>;
      return p["job_id"] === importId;
    }) ?? null;
  };
  const who = (email: string | null | undefined) =>
    (accounts.data ?? []).find((a) => a.email === email)?.label ?? email ?? "";

  const undo = (j: Job) => {
    const d = importData(j);
    const made = d ? d.rows - d.failed : null;
    ask({
      title: "Undo this import?",
      says: `${made !== null ? `${made.toLocaleString()} rows were loaded. ` : ""}Records it made are removed and values it changed go back. A record another source also has stays.`,
      label: "Undo the import",
      danger: true,
      run: async () => {
        const id = await callFunction<string>(token, "undo_import", { p_job_id: j.id });
        setUndoJobId(id);
        queryClient.invalidateQueries({ queryKey: ["import-undos"] });
      },
    });
  };

  const count = (j: Job, k: keyof Counts) => {
    const c = counts.data?.get(j.id ?? "");
    if (!c) return counts.isLoading ? "" : <span className="muted">-</span>;
    return c[k] ? c[k].toLocaleString() : <span className="muted">0</span>;
  };

  const columns: ColumnSpec<Job>[] = [
    { id: "file", header: "File", role: "leading", fields: [], title: true, narrowLabel: null,
      cell: (j) => {
        const p = (j.params ?? {}) as Record<string, unknown>;
        const key = typeof p["key"] === "string" ? p["key"] : "";
        const name = key.split("/").pop() || "a file";
        // The values for every row an import filled, from its own summary.
        const marked = jobSummary(j).filter((line) => line.startsWith("Marked "));
        const same = importData(j)?.sameFileAs;
        return (
          <span className="im-file">
            {j.id && j.status === "done" && !same ? <Link to={`/imports/${j.id}`}>{name}</Link> : name}
            {marked.length ? <span className="muted im-marked">{marked.join(" ")}</span> : null}
            {same ? (
              <span className="muted im-marked">
                The same file as the import of {same.loadedAt ? shortDate(same.loadedAt) : "an earlier day"}, which
                has its records and its Undo.
              </span>
            ) : null}
          </span>
        );
      } },
    { id: "type", header: "Type", role: "secondary", fields: [], narrowLabel: null,
      cell: (j) => {
        const p = (j.params ?? {}) as Record<string, unknown>;
        const f = typeof p["format"] === "string" ? p["format"] : "";
        return FORMAT_WORDS[f] ?? f;
      } },
    { id: "status", header: "Status", role: "leading", fields: [], narrowLabel: null,
      cell: (j) => {
        const u = undoOf(j.id);
        if (u?.status === "done") return <Badge tone="quiet">Undone</Badge>;
        if (u && (u.status === "queued" || u.status === "running")) return <Badge tone="waiting">Undoing</Badge>;
        if (u?.status === "failed") return <span className="refusal">The undo failed. {u.error}</span>;
        if (j.status === "failed") return <span className="refusal">{j.error ?? "Failed"}</span>;
        if (j.status === "done") return <Badge tone="ok">Loaded</Badge>;
        return <Badge tone="waiting">{j.status === "running" ? "Loading" : "Queued"}</Badge>;
      } },
    { id: "added", header: "Added", role: "secondary", fields: [], align: "num", narrowLabel: "added", cell: (j) => count(j, "added") },
    { id: "updated", header: "Updated", role: "secondary", fields: [], align: "num", narrowLabel: "updated", cell: (j) => count(j, "updated") },
    { id: "matched", header: "Matched", role: "detail", fields: [], align: "num", narrowLabel: "matched", cell: (j) => count(j, "matched") },
    { id: "held", header: "Held for review", role: "detail", fields: [], align: "num", narrowLabel: "held for review", cell: (j) => count(j, "held") },
    { id: "by", header: "Loaded by", role: "secondary", fields: [], narrowLabel: null, cell: (j) => <span className="im-nowrap">{who(j.requested_by)}</span> },
    { id: "when", header: "When", role: "secondary", fields: [], narrowLabel: null,
      cell: (j) => <span className="im-nowrap">{shortDate(j.finished_at ?? j.queued_at)}</span> },
  ];

  const rowMenu = (j: Job) => {
    const u = undoOf(j.id);
    // The engine's rule (migration 246): an import can be undone when its
    // loader recorded its signal on the job, which every loader does once it
    // has committed. A job with rows that failed has one too, because the
    // rest of its rows are in the lake.
    // An import of a file loaded before, while that import stands, has its
    // records and Undo there (engine migration 300), so it offers neither.
    const same = Boolean(importData(j)?.sameFileAs);
    const signal = Boolean(importData(j)?.signalId) && !same;
    const canUndo = signal && (j.status === "done" || j.status === "failed")
      && (!u || u.status === "failed");
    return (
      <span className="im-actions">
        {(j.status === "done" || signal) && j.id && !same ? <Link className="im-records" to={`/imports/${j.id}`}>Records</Link> : null}
        {j.status === "done" && signal && j.id && u?.status !== "done"
          ? <Link className="im-records" to={`/imports/${j.id}/compare`}>Compare</Link> : null}
        {canUndo ? <Button kind="danger" size="compact-sm" onClick={() => undo(j)}>Undo</Button> : null}
      </span>
    );
  };

  const chips = params.filters.format
    ? [{ label: `Type: ${FORMAT_WORDS[params.filters.format] ?? params.filters.format}`,
         onRemove: () => params.set({ filters: {} }) }]
    : [];
  const n = total.data ?? null;
  const lastPage = n === null ? params.page : Math.max(1, Math.ceil(n / PAGE_SIZE));

  return (
    <section className="im-history" aria-labelledby="im-history-h">
      <h2 id="im-history-h">
        What has been imported{" "}
        {n !== null ? <span className="muted ui-section-count">({n.toLocaleString()})</span> : null}
      </h2>
      {undoJobId ? (
        <JobProgress key={undoJobId} token={token} jobId={undoJobId}
                     waiting="Undo queued." running="Undoing the import." done="Undone."
                     onDone={() => {
                       queryClient.invalidateQueries({ queryKey: ["import-undos"] });
                       queryClient.invalidateQueries({ queryKey: ["imports"] });
                       queryClient.invalidateQueries({ queryKey: ["import-counts"] });
                     }} />
      ) : null}
      <Toolbar
        id="imports"
        search={{ label: "Search by file name", value: params.search, onSubmit: (s) => params.set({ search: s }) }}
        filtersCount={chips.length}
        filtersOpen={filtersOpen}
        onToggleFilters={() => setFiltersOpen((v) => !v)}
        chips={chips}
        onClearAll={() => params.set({ search: "", filters: {} })}
      />
      <FiltersPanel id="imports" open={filtersOpen} onDone={() => setFiltersOpen(false)}>
        <div className="filters-own">
          <NativeSelect label="Type" value={params.filters.format ?? ""}
                        data={[{ value: "", label: "Any" },
                               ...Object.entries(FORMAT_WORDS).map(([value, label]) => ({ value, label }))]}
                        onChange={(e) => params.set({ filters: { format: e.currentTarget.value } })} />
        </div>
      </FiltersPanel>
      {counts.isError ? (
        <p className="refusal" role="alert">The record counts did not load. {(counts.error as Error).message}</p>
      ) : null}
      {history.isLoading ? <p className="muted"><Spinner />Loading.</p>
        : history.isError ? (
          <p className="refusal" role="alert">
            {(history.error as Error).message} <Button size="compact-sm" onClick={() => history.refetch()}>Try again</Button>
          </p>
        ) : !rows.length ? (
          <p className="muted">
            {params.search || chips.length
              ? <>No import matches. <Button kind="link" size="compact-sm" onClick={() => params.set({ search: "", filters: {} })}>Clear</Button></>
              : "No imports yet. The first file goes above."}
          </p>
        ) : (
          <DataTable columns={columns} rows={rows} params={params} rowKey={(j) => j.id ?? ""} rowMenu={rowMenu} />
        )}
      {lastPage > 1 ? (
        <div className="bar im-pager">
          <Button onClick={() => params.set({ page: params.page - 1 })} disabled={params.page <= 1}>Previous</Button>
          <span className="muted">Page {params.page.toLocaleString()} of {lastPage.toLocaleString()}</span>
          <Button onClick={() => params.set({ page: params.page + 1 })} disabled={params.page >= lastPage}>Next</Button>
        </div>
      ) : null}
      {dialog}
    </section>
  );
}
